import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

/**
 * Поиск букета для добавления в заказ (админ-флоу).
 *
 * Props:
 *  - onSelect({bouquet_id, name, price, components, available_for_date, available_physical, components_status}): обработчик выбора
 *  - onClose(): закрытие dropdown
 *  - deliveryDate: 'YYYY-MM-DD' — дата для расчёта availability per bouquet
 *  - excludeBouquetIds: [int] — id букетов уже в заказе (показаны как «уже в заказе»)
 *  - excludeOrderId: int|null — для edit-flow вычитаем OIRs текущего заказа из «занятых»
 *  - maxHeight: число
 *
 * Рендер карточки:
 *   Сильва + Шарм       555 ₽
 *   Готовы к сборке: 5 (1)         ← N (M) где N=total, M=physical_only
 *
 *   Подарочный (нет в наличии)     ← красным
 *   1200 ₽ · Нет в наличии
 */
function BouquetSearchDropdown({
  onSelect,
  onClose,
  deliveryDate = null,
  excludeBouquetIds = [],
  excludeOrderId = null,
  maxHeight = 450,
  // cartItemsPending = уже добавленные позиции строящегося/редактируемого заказа.
  // Формат: [{product_id, quantity} | {bouquet_id, quantity, components: [{product_id, quantity}]}].
  // Backend вычтет их спрос из per-component avail чтобы не показывать «доступно» с учётом
  // тех стеблей, которые уже зарезервировал админ другими позициями.
  cartItemsPending = [],
}) {
  const [bouquets, setBouquets] = useState([]);
  const [availMap, setAvailMap] = useState({}); // {bid: {physical_available, recipe_capacity, total_available, components}}
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const wrapperRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const data = await api.getBouquetsAdmin(false);
        const items = data.items || [];
        if (cancelled) return;
        setBouquets(items);
        // Запрашиваем availability batch на дату (если указана), передавая cart-in-progress
        // чтобы спрос уже-добавленных позиций был учтён в per-component avail.
        if (deliveryDate && items.length > 0) {
          try {
            const res = await api.getBouquetsAvailableMaxQtyBatch(
              items.map(b => b.id),
              deliveryDate,
              excludeOrderId,
              cartItemsPending,
            );
            if (!cancelled) setAvailMap(res.results || {});
          } catch { /* ignore */ }
        }
      } catch {
        if (!cancelled) setBouquets([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deliveryDate, excludeOrderId, JSON.stringify(cartItemsPending)]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const filtered = bouquets
    .filter(b => b.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      // Сначала с наличием, потом без — внутри по имени
      const av = availMap[String(a.id)];
      const bv = availMap[String(b.id)];
      const aStock = (av?.total_available ?? 0) > 0 ? 0 : 1;
      const bStock = (bv?.total_available ?? 0) > 0 ? 0 : 1;
      if (aStock !== bStock) return aStock - bStock;
      return a.name.localeCompare(b.name, 'ru');
    });

  return (
    <div ref={wrapperRef} style={{
      border: '1px solid #ddd', '--ch': '6px', '--stroke': '#ddd', background: '#fff',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden'
    }}>
      <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Поиск букета..."
          autoFocus
          style={{
            width: '100%', padding: '6px 8px', fontSize: 13,
            border: '1px solid #ddd', '--ch': '4px', '--stroke': '#ddd', outline: 'none',
            boxSizing: 'border-box'
          }}
        />
      </div>
      <div style={{ maxHeight, overflowY: 'auto' }}>
        {isLoading ? (
          <div style={{ padding: 12, textAlign: 'center', color: '#999', fontSize: 13 }}>Загрузка...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 12, textAlign: 'center', color: '#999', fontSize: 13 }}>Букетов нет</div>
        ) : filtered.map(bouquet => {
          const alreadyInOrder = excludeBouquetIds.includes(bouquet.id);
          const av = availMap[String(bouquet.id)] || { physical_available: 0, physical_now: 0, recipe_capacity: 0, total_available: 0, components: [] };
          const total = av.total_available;
          const phys = av.physical_available;
          const recipe = av.recipe_capacity || 0;
          const physNow = av.physical_now || 0;
          const noStock = total <= 0 && !deliveryDate
            ? false  // если дата не выбрана — не показываем «нет в наличии», просто без подписи
            : total <= 0;
          // Собран сегодня, но партия истечёт к дате доставки — подсказать причину недоступности
          const expiresByDate = noStock && physNow > 0;
          const disabled = alreadyInOrder;
          const availLabel = noStock
            ? (expiresByDate ? ` · Нет на дату (собрано ${physNow} шт, истечёт к дате)` : ' · Нет в наличии')
            : ' · ' + [
                phys > 0 ? `Собрано: ${phys}` : null,
                recipe > 0 ? `Соберём: ${recipe}` : null,
              ].filter(Boolean).join(' · ');

          // payload для onSelect: snapshot компонентов из bouquet.components + data из availability
          const handlePick = () => {
            if (disabled) return;
            onSelect({
              bouquet_id: bouquet.id,
              name: bouquet.name,
              price: bouquet.price,
              kind: bouquet.kind,
              components: (bouquet.components || []).map(c => ({
                product_id: c.product_id,
                product_name: c.product_name,
                product_price: c.product_price,
                quantity: c.quantity,
                sort_order: c.sort_order || 0,
                is_replacement: false,
              })),
              available_for_date: total,
              available_physical: phys,
              assembled_only: !!bouquet.assembled_only,
              components_status: av.components || [],
            });
          };

          return (
            <div key={bouquet.id}
              onClick={handlePick}
              style={{
                padding: '8px 10px', cursor: disabled ? 'default' : 'pointer',
                borderBottom: '1px solid #F4F4EF',
                opacity: disabled ? 0.4 : 1,
                background: noStock && !disabled ? '#FEF6E6' : '#fff',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: 13,
              }}
              onMouseEnter={e => !disabled && (e.currentTarget.style.background = noStock ? '#F9EBCF' : '#F4F4EF')}
              onMouseLeave={e => e.currentTarget.style.background = noStock && !disabled ? '#FEF6E6' : '#fff'}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, color: noStock ? '#9A4820' : undefined }}>
                  {bouquet.name}
                </div>
                <div style={{ fontSize: 11, color: noStock ? '#9A4820' : '#666' }}>
                  {bouquet.price} ₽
                  {deliveryDate && availLabel}
                  {alreadyInOrder && ' · Уже в заказе'}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default BouquetSearchDropdown;
